import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import HfpV2Report from '../../components/hfp/HfpV2Report';
import { apiHfpTestGenerateReport } from '../../utils/hfpTestApi';
import { loadHfpTestSession } from './hfpTestSession';

/**
 * HfpTestReportPage
 * -----------------
 * Staging-only mirror of RmsServiceUserHfpPage for the HFP test site
 * (/rms/hfp-test/report). Generates (or loads the cached) HFP v2 report for
 * the tester's demo record via the rmsHfpTestGenerateReport callable — the
 * same report engine behind the production profile and the HFP v2 demo —
 * and renders it with the same HfpV2Report component service users see.
 */
export default function HfpTestReportPage() {
  const navigate = useNavigate();
  const session = loadHfpTestSession();
  const patientRecordId = session?.patientRecordId;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReport = useCallback(async () => {
    if (!patientRecordId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiHfpTestGenerateReport({ patientRecordId });
      if (res?.report) setReport(res.report);
      else if (res?.status === 'not_ready') setError('The profile is not ready yet — it appears once all forms are complete.');
      else setError('No report was returned. Please try again shortly.');
    } catch (err) {
      setError(err?.message || 'Could not load the profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [patientRecordId]);

  useEffect(() => { loadReport(); }, [loadReport]);

  if (!session) {
    return <Navigate to="/rms/hfp-test" replace />;
  }

  const childName = session.childFirstName || 'the child';

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-primary/5 via-white to-secondary/15">
      {/* Slim top bar with a route back to the test dashboard (mirrors
         RmsServiceUserHfpPage — view only, no download or regenerate). */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-6 py-2 border-b border-gray-200 bg-white/70">
        <button
          type="button"
          onClick={() => navigate('/rms/hfp-test/dashboard')}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          &larr; Back
        </button>
      </div>

      <div className="flex-1 min-h-0 px-4 sm:px-6 py-4 overflow-hidden">
        <div className="h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          {loading && (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500 p-8 text-center">
              Preparing the profile — this can take a minute or two the first time.
            </div>
          )}
          {error && !loading && (
            <div className="flex-1 flex items-center justify-center text-sm text-red-600 p-8 text-center">{error}</div>
          )}
          {report && !loading && !error && (
            <div className="flex-1 min-h-0">
              <HfpV2Report report={report} childName={childName} pageMode />
            </div>
          )}
        </div>
      </div>

      <footer className="flex-shrink-0 border-t border-gray-200 bg-white px-4 sm:px-6 py-3">
        <p className="text-[11px] leading-relaxed text-gray-500 text-center max-w-4xl mx-auto">
          This is not a medical assessment and does not constitute a diagnosis, and cannot be
          used to claim or deny any medical condition.
        </p>
      </footer>
    </div>
  );
}
